import { TokenHistoryInfo } from "../../../types/history";
import { TokenMetadata } from "../../../store/global/getTokenMetadata";
import { Token } from "../../../store/global/TokenEnum";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { TokensSecondRow } from "./TokensSecondRow";
import { TokensFirstRow } from "./TokensFirstRow";
import { useMemo } from "react";
import { SEPARATOR_DOT } from "../../../config/consts";
import { Amount } from "types/amount";
import { useIsTradeHistoryReturned } from "hooks/useIsTradeHistoryReturned";
import { SkeletonBlock } from "components/general/Skeleton";
import styled from "@emotion/styled";
import { getNameForToken } from "../../../utils/tokenMetadataUtils";
import { ActivityStore } from "store/activity/ActivityStore";

interface TokenNamesProps {
  tokenHistoryInfo: TokenHistoryInfo;
}

export const TokensBoughtOrSold: React.FC<TokenNamesProps> = ({ tokenHistoryInfo }) => {
  const tokens = ActivityStore.useState(s => s.tokens);
  const historyTokens = Object.keys(tokenHistoryInfo) as Array<Token>;
  const theme = GlobalStore.useState(s => s.theme);
  const isTradeHistoryReturned = useIsTradeHistoryReturned();
  const fullName = getNameForToken(Object.keys(tokenHistoryInfo)[0] as Token, tokens);

  // If there's more than 1 token we just show X Tokens else we show the full name of the 1 Token
  const header = useMemo(() => {
    if (historyTokens.length > 1) {
      return `${historyTokens.length} Tokens`;
    } else {
      return fullName;
    }
  }, [historyTokens.length, fullName]);

  return (
    <div style={{ marginLeft: "8px" }}>
      <TokensFirstRow theme={theme}>
        {isTradeHistoryReturned ? (
          header
        ) : (
          <SkeletonFirstRow>
            <SkeletonBlock />
          </SkeletonFirstRow>
        )}
      </TokensFirstRow>
      <TokensSecondRow theme={theme}>
        {isTradeHistoryReturned ? (
          getTokensBoughtOrSoldDescription(
            historyTokens,
            tokenHistoryInfo[historyTokens[0]]?.amount,
            tokens,
            false
          )
        ) : (
          <SkeletonSecondRow>
            <SkeletonBlock />
          </SkeletonSecondRow>
        )}
      </TokensSecondRow>
    </div>
  );
};

// Sub-components

const SkeletonFirstRow = styled.div`
  width: 85px;
  height: 12px;
`;

const SkeletonSecondRow = styled.div`
  margin-top: 4px;
  width: 129px;
  height: 11px;
`;

// Helpers

export const getTokensBoughtOrSoldDescription = (
  tokens: Array<Token>,
  firstAmount: Amount | undefined,
  tokenMetadata: TokenMetadata | undefined,
  displayAllTokens: boolean
) => {
  if (tokens.length > 1 || firstAmount == null) {
    if (displayAllTokens || tokens.length < 5) {
      return tokens.map(symbol => symbol).join(` ${SEPARATOR_DOT} `);
    }
    const customDescription = [...tokens.slice(0, 2), `+${tokens.length - 2}`];
    return customDescription.join(` ${SEPARATOR_DOT} `);
  }

  const firstAmountFormatted: string | undefined = firstAmount
    .toDisplay(tokens[0], tokenMetadata)
    .toString();

  return `${firstAmountFormatted} ${tokens[0]}`;
};
